<template>
  <section class="quote-submissions">
    <h2>Submitted Quotes</h2>
    <div
      class="status-filters"
      role="group"
      aria-label="Submission status"
    >
      <button
        v-for="status in statuses"
        :key="status.value"
        type="button"
        :aria-pressed="activeStatus === status.value ? 'true' : 'false'"
        @click="setStatus(status.value)"
      >
        {{ status.label }}
      </button>
    </div>
    <p
      v-if="actionError"
      role="alert"
      class="action-error"
    >
      {{ actionError }}
    </p>
    <p
      v-if="loading"
      role="status"
    >
      Loading submitted quotes...
    </p>
    <div
      v-else-if="loadError"
      role="alert"
      class="load-error"
    >
      <p>{{ loadError }}</p>
      <button
        type="button"
        @click="loadSubmissions"
      >
        Try again
      </button>
    </div>
    <p v-else-if="submissions.length === 0">
      {{ emptyMessage }}
    </p>
    <article
      v-for="submission in submissions"
      :key="submission.id"
      :data-submission-id="submission.id"
      class="submission-card"
    >
      <div class="submission-card__heading">
        <span
          class="submission-card__status"
          :class="`status--${submission.status}`"
        >
          {{ statusLabel(submission.status) }}
        </span>
        <time :datetime="submission.created_at">
          {{ formatDate(submission.created_at) }}
        </time>
      </div>
      <blockquote>{{ submission.quote }}</blockquote>
      <dl>
        <div>
          <dt>Quote author:</dt>
          <dd>{{ optionalValue(submission.quoteAuthor) }}</dd>
        </div>
        <div>
          <dt>Submitter:</dt>
          <dd>{{ optionalValue(submission.submitterName) }}</dd>
        </div>
        <div>
          <dt>Email:</dt>
          <dd>{{ optionalValue(submission.submitterEmail) }}</dd>
        </div>
        <div>
          <dt>Acknowledgment:</dt>
          <dd>{{ acknowledgmentLabel(submission.allowAcknowledgment) }}</dd>
        </div>
        <div v-if="submission.reviewedAt">
          <dt>Reviewed:</dt>
          <dd>{{ formatDate(submission.reviewedAt) }}</dd>
        </div>
      </dl>
      <div
        v-if="submission.status === 'pending'"
        class="submission-card__actions"
      >
        <button
          type="button"
          :disabled="transitioningId === submission.id"
          @click="moderate(submission, 'approved')"
        >
          Approve
        </button>
        <button
          type="button"
          :disabled="transitioningId === submission.id"
          @click="moderate(submission, 'denied')"
        >
          Deny
        </button>
      </div>
    </article>
  </section>
</template>

<script>
export default {
  name: 'project-quote-submissions',
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeStatus: 'pending',
      submissions: [],
      loading: false,
      loadError: '',
      actionError: '',
      transitioningId: null,
      transitionGeneration: 0,
      latestRequest: null,
      statuses: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'denied', label: 'Denied' },
      ],
    };
  },
  watch: {
    'project.id': function handleProjectChange() {
      this.transitionGeneration += 1;
      this.activeStatus = 'pending';
      this.submissions = [];
      this.loadError = '';
      this.actionError = '';
      this.transitioningId = null;
      this.loadSubmissions();
    },
  },
  computed: {
    jwt() {
      return this.$store.state.profile.jwt;
    },
    emptyMessage() {
      return `No ${this.activeStatus} submissions.`;
    },
  },
  methods: {
    setStatus(status) {
      if (!this.statuses.some(option => option.value === status)) return;
      if (status === this.activeStatus) return;
      this.activeStatus = status;
      this.submissions = [];
      this.loadSubmissions();
    },
    statusLabel(status) {
      const option = this.statuses.find(item => item.value === status);
      return option ? option.label : status;
    },
    optionalValue(value) {
      return value || 'Not provided';
    },
    acknowledgmentLabel(value) {
      if (value === null || value === undefined) return 'Not recorded';
      return value ? 'Yes' : 'No';
    },
    formatDate(value) {
      return new Date(value).toLocaleString();
    },
    moderate(submission, status) {
      if (this.transitioningId !== null) return;
      if (!['approved', 'denied'].includes(status)) return;
      const action = status === 'approved' ? 'Approve' : 'Deny';
      // Native confirmation is intentional for irreversible moderation decisions.
      // eslint-disable-next-line no-alert
      if (!window.confirm(`${action} this submitted quote?`)) return;

      const projectId = this.project.id;
      this.transitionGeneration += 1;
      const { transitionGeneration } = this;
      this.actionError = '';
      this.transitioningId = submission.id;
      this.axios({
        method: 'PUT',
        url: `${this.$API}/projects/${projectId}/quote-submissions/${submission.id}/moderation`,
        headers: {
          Authorization: `Bearer ${this.jwt}`,
        },
        data: { status },
      }).then(() => {
        if (this.project.id !== projectId
          || this.transitionGeneration !== transitionGeneration) return null;
        return this.loadSubmissions();
      })
        .then(() => {
          if (this.project.id === projectId
            && this.transitionGeneration === transitionGeneration) {
            this.transitioningId = null;
          }
        })
        .catch((error) => {
          if (this.project.id !== projectId
            || this.transitionGeneration !== transitionGeneration) return null;
          const statusCode = error && error.response && error.response.status;
          if (statusCode === 409 || statusCode === 404) {
            this.actionError = statusCode === 409
              ? 'This submission was already reviewed. Refreshing the queue.'
              : 'This submission is no longer available. Refreshing the queue.';
            return this.loadSubmissions().then(() => {
              if (this.project.id === projectId
                && this.transitionGeneration === transitionGeneration) {
                this.transitioningId = null;
              }
            });
          }
          this.transitioningId = null;
          this.actionError = statusCode === 403
            ? 'You are not authorized to moderate quotes.'
            : 'The moderation decision could not be saved. Try again.';
          return null;
        });
    },
    loadSubmissions() {
      const projectId = this.project.id;
      const status = this.activeStatus;
      const request = {};
      this.latestRequest = request;
      this.loading = true;
      this.submissions = [];
      this.loadError = '';
      return this.axios.get(
        `${this.$API}/quote-submissions/moderation?projectId=${encodeURIComponent(projectId)}&status=${status}`,
        {
          headers: {
            Authorization: `Bearer ${this.jwt}`,
          },
        },
      ).then((response) => {
        if (this.latestRequest !== request
          || this.project.id !== projectId || this.activeStatus !== status) return;
        if (!response.data || !Array.isArray(response.data.submissions)) {
          throw new Error('Invalid moderation response');
        }
        this.submissions = response.data.submissions;
        this.loading = false;
      }).catch((error) => {
        if (this.latestRequest !== request
          || this.project.id !== projectId || this.activeStatus !== status) return;
        this.loading = false;
        this.loadError = error && error.response && error.response.status === 403
          ? 'You are not authorized to moderate quotes.'
          : 'Submitted quotes could not be loaded.';
      });
    },
  },
  created() {
    this.loadSubmissions();
  },
};
</script>

<style lang="scss" scoped>
.quote-submissions {
  max-width: 100%;
  min-width: 0;
  text-align: left;
}

.status-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;

  button[aria-pressed='true'] {
    background-color: #000;
    color: #fff;
    font-weight: 700;
  }
}

.submission-card {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-bottom: 1rem;
  max-width: 100%;
  overflow-wrap: anywhere;
  padding: 1rem;

  blockquote {
    font-size: 1.1rem;
    margin: 1rem 0;
    white-space: pre-wrap;
  }

  dl {
    margin: 0;
  }

  dl > div {
    display: grid;
    gap: 0.5rem;
    grid-template-columns: minmax(7rem, 9rem) minmax(0, 1fr);
    margin-top: 0.5rem;
  }

  dd {
    margin: 0;
    min-width: 0;
  }

  dt {
    font-weight: 700;
  }
}

.submission-card__heading {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
}

.submission-card__actions {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-top: 1rem;

  button {
    flex: 0 0 auto;
    width: auto;
  }
}

.submission-card__status {
  border-radius: 999px;
  display: inline-block;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
}

.status--pending {
  background-color: #fff3cd;
  color: #856404;
}

.status--approved {
  background-color: #d4edda;
  color: #155724;
}

.status--denied {
  background-color: #f8d7da;
  color: #721c24;
}
</style>
